import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["input", "dropdown", "option", "moreIndicator", "moreCount", "noResults"]
  static values = {
    basePath: String,
    turboFrame: { type: String, default: "" }
  }

  connect() {
    this.isOpen = false
    this.selectedIndex = -1
    
    // Close dropdown when clicking outside
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  // Filter options based on input text
  filter() {
    const query = this.inputTarget.value.toLowerCase().trim()
    this.filterOptions(query)
    this.open()
  }

  // Open dropdown on focus
  focus() {
    this.inputTarget.select()
    this.filterOptions("")
    this.open()
  }

  // Filter and show/hide options
  filterOptions(query) {
    let totalMatches = 0
    const isShowAll = query === ""

    this.optionTargets.forEach((option) => {
      const title = option.dataset.officeTitle.toLowerCase()
      const matches = isShowAll || title.includes(query)
      
      if (matches) {
        totalMatches++
        option.classList.remove("hidden")
      } else {
        option.classList.add("hidden")
      }
    })

    // We no longer cap visible results, so the "more" indicator is always hidden.
    this.moreIndicatorTarget.classList.add("hidden")

    // Handle "no results" message
    if (totalMatches === 0) {
      this.noResultsTarget.classList.remove("hidden")
    } else {
      this.noResultsTarget.classList.add("hidden")
    }

    this.selectedIndex = -1
  }

  // Handle keyboard navigation
  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.navigateDown()
        break
      case "ArrowUp":
        event.preventDefault()
        this.navigateUp()
        break
      case "Enter":
        event.preventDefault()
        this.selectCurrent()
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  // Get visible options
  get visibleOptions() {
    return this.optionTargets.filter(opt => !opt.classList.contains("hidden"))
  }

  // Navigate down in the dropdown
  navigateDown() {
    const visible = this.visibleOptions
    if (this.selectedIndex < visible.length - 1) {
      this.selectedIndex++
      this.highlightOption()
    }
  }

  // Navigate up in the dropdown
  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--
      this.highlightOption()
    }
  }

  // Highlight the currently selected option
  highlightOption() {
    const visible = this.visibleOptions
    visible.forEach((option, index) => {
      const link = option.querySelector("a")
      if (index === this.selectedIndex) {
        link?.classList.add("bg-base-200")
      } else {
        link?.classList.remove("bg-base-200")
      }
    })
  }

  // Select the current highlighted option
  selectCurrent() {
    const visible = this.visibleOptions
    if (this.selectedIndex >= 0 && this.selectedIndex < visible.length) {
      const option = visible[this.selectedIndex]
      this.selectOfficeFromElement(option)
    }
  }

  // Select an office from a click event
  selectOption(event) {
    event.preventDefault()
    const option = event.currentTarget.closest("[data-office-id]")
    this.selectOfficeFromElement(option)
  }

  // Select an office from element data attributes
  selectOfficeFromElement(element) {
    const officeId = element.dataset.officeId
    const officeTitle = element.dataset.officeTitle
    this.selectOffice(officeId, officeTitle)
  }

  // Select an office and navigate
  selectOffice(officeId, officeTitle) {
    this.inputTarget.value = officeTitle
    this.close()
    
    // Build the URL and navigate using Turbo
    const url = `${this.basePathValue}?office_id=${officeId}`
    if (this.turboFrameValue) {
      Turbo.visit(url, { frame: this.turboFrameValue })
    } else {
      Turbo.visit(url)
    }
  }

  // Open the dropdown
  open() {
    this.isOpen = true
    this.dropdownTarget.classList.remove("hidden")
    this.selectedIndex = -1
  }

  // Close the dropdown
  close() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
    this.selectedIndex = -1
    // Remove highlights
    this.optionTargets.forEach(opt => {
      opt.querySelector("a")?.classList.remove("bg-base-200")
    })
  }

  // Handle clicks outside the component
  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }
}
